import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRightLeft, TrendingUp, RefreshCw } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CURRENCIES = [
  { code: "USD", name: "US Dollar" },
  { code: "EUR", name: "Euro" },
  { code: "GBP", name: "British Pound" },
  { code: "AED", name: "UAE Dirham" },
  { code: "INR", name: "Indian Rupee" },
  { code: "SGD", name: "Singapore Dollar" },
  { code: "AUD", name: "Australian Dollar" },
  { code: "CAD", name: "Canadian Dollar" },
  { code: "JPY", name: "Japanese Yen" },
  { code: "CNY", name: "Chinese Yuan" },
  { code: "SAR", name: "Saudi Riyal" },
  { code: "ZAR", name: "South African Rand" },
];

const WATCH = ["EUR", "GBP", "AED", "INR", "SGD", "CNY"];

export default function CurrencyConverter() {
  const [rates, setRates] = useState(null); // rates relative to USD
  const [updated, setUpdated] = useState("");
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("1000");
  const [from, setFrom] = useState("USD");
  const [to, setTo] = useState("INR");

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/currency/rates?base=USD`);
      setRates(res.data.rates);
      setUpdated(res.data.updated || "");
    } catch (e) {
      // silent — widget degrades gracefully
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const convert = (amt, f, t) => {
    if (!rates || !rates[f] || !rates[t]) return null;
    const usd = Number(amt || 0) / rates[f];
    return usd * rates[t];
  };

  const result = useMemo(() => convert(amount, from, to), [amount, from, to, rates]);

  const fmt = (v, code) =>
    v == null ? "—" : new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(v) + " " + code;

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <section id="currency" data-testid="currency-section" className="relative py-24 md:py-32 bg-[#F9F8F6]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          <motion.div
            className="lg:col-span-5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="overline">Trade Tools</p>
            <h2 className="mt-4 font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1A3626] leading-[1.05] tracking-tight">
              Live currency<br /><span className="italic text-[#C5A059]">converter.</span>
            </h2>
            <p className="mt-6 text-[#4B5563] text-lg leading-relaxed max-w-md">
              Price your shipment in the currency your market speaks. Rates refresh from
              global markets so your negotiations start on solid ground.
            </p>

            <div className="mt-10 grid grid-cols-3 gap-4 max-w-md" data-testid="currency-watchlist">
              {WATCH.map((c) => {
                const v = convert(1, "USD", c);
                return (
                  <div key={c} className="border-t border-[#1A3626]/15 pt-3">
                    <div className="overline text-[#1A3626]/50">USD / {c}</div>
                    <div className="mt-1 font-serif-display text-2xl text-[#1A3626]">
                      {v == null ? "—" : v.toFixed(c === "INR" || c === "JPY" ? 2 : 3)}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          >
            <div className="bg-[#1A3626] text-white p-8 md:p-12 relative overflow-hidden" data-testid="currency-converter-card">
              <div className="absolute inset-0 grain"></div>
              <div className="relative">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#E6C97A]">
                    <TrendingUp size={18} strokeWidth={1.5} />
                    <span className="overline text-[#E6C97A]">Currency Exchange</span>
                  </div>
                  <button
                    onClick={load}
                    data-testid="currency-refresh"
                    className="text-white/60 hover:text-[#E6C97A] transition-colors"
                    aria-label="Refresh rates"
                  >
                    <RefreshCw size={16} strokeWidth={1.5} className={loading ? "animate-spin" : ""} />
                  </button>
                </div>

                <div className="mt-8">
                  <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">Amount</label>
                  <Input
                    data-testid="currency-amount"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="rounded-none bg-white/5 border-white/20 text-white text-2xl h-16 font-serif-display focus-visible:ring-[#C5A059]"
                  />
                </div>

                <div className="mt-6 grid grid-cols-1 sm:grid-cols-[1fr_auto_1fr] items-end gap-4">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">From</label>
                    <CurrencySelect value={from} onChange={setFrom} testid="currency-from" />
                  </div>
                  <button
                    onClick={swap}
                    data-testid="currency-swap"
                    className="hidden sm:flex items-center justify-center h-11 w-11 border border-white/20 text-[#E6C97A] hover:bg-white/10 transition-colors mb-0"
                    aria-label="Swap currencies"
                  >
                    <ArrowRightLeft size={18} strokeWidth={1.5} />
                  </button>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">To</label>
                    <CurrencySelect value={to} onChange={setTo} testid="currency-to" />
                  </div>
                </div>

                <div className="mt-8 border-t border-white/10 pt-6">
                  <div className="overline text-white/40">Converted amount</div>
                  <div
                    data-testid="currency-result"
                    className="mt-2 font-serif-display text-4xl md:text-5xl text-[#E6C97A] break-words"
                  >
                    {fmt(result, to)}
                  </div>
                  <div className="mt-3 text-xs text-white/40">
                    1 {from} = {result != null ? (convert(1, from, to)).toFixed(4) : "—"} {to}
                    {updated ? ` · ${updated}` : ""}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function CurrencySelect({ value, onChange, testid }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="rounded-none bg-white/5 border-white/20 text-white h-11 focus:ring-[#C5A059]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {CURRENCIES.map((c) => (
          <SelectItem key={c.code} value={c.code}>
            {c.code} · {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
